<template>
  <PaymentNotifications />
  <ContainerLayout>
    <div
      class="tw:min-h-(--tw-min-height-screen-minus-header-and-footer) tw:flex-col tw:flex tw:w-full tw:md:max-w-(--tw-breakpoint-lg) tw:lg:max-w-(--tw-breakpoint-xl) tw:mr-auto tw:ml-auto tw:mb-8"
    >
      <AppMenu :data="tabs" :loading="false" class="tw:w-full" />
      <router-view />
    </div>
  </ContainerLayout>
</template>

<script setup lang="ts">
import { ref, watch, computed, type Ref, type ComputedRef } from 'vue'
import { isNavigationFailure, useRouter } from 'vue-router'
import * as Sentry from '@sentry/vue'

import AppMenu from '@/components/appMenu/AppMenu.vue'
import PaymentNotifications from '@/components/notifications/PaymentNotifications.vue'
import ContainerLayout from '@/layouts/ContainerLayout.vue'

import { debounce } from '@/utils/Utility'
import { useZuoraAccount } from '@/components/payment/queries'
import { useSessionValues } from '@/composables/useSessionValues'

/** Tab configuration for billing menu */
interface BillingTab {
  label: string
  route: string
  testId: string
}

const router = useRouter()
const { hasAdminOrSupport } = useSessionValues()

const { hasNoBillingInfo, isInitialLoading } = useZuoraAccount()

/** Loading state for Zuora account data */
const isDataLoading: ComputedRef<boolean> = computed(() => isInitialLoading.value)

/** Billing section tabs */
const tabs: Ref<BillingTab[]> = ref([
  {
    label: 'tbs_premium_products',
    route: '/billing/premium-products',
    testId: 'billing-premium-products',
  },
  {
    label: 'tbs_invoicing_invoices',
    route: '/billing/invoices',
    testId: 'billing-invoices',
  },
  {
    label: 'paymentMethods.edit.cta.tabTitle',
    route: '/billing/payment-methods',
    testId: 'billing-payment-methods',
  },
])

/**
 * Debounced navigation to prevent rapid route changes
 * @param path - Target route path
 */
const debouncedNavigate = debounce(async (path: string): Promise<void> => {
  try {
    await router.push(path)
  } catch (error) {
    if (!isNavigationFailure(error)) {
      Sentry.withScope(scope => {
        scope.setTag('feature', 'Billing Redirect')
        Sentry.captureException(error)
      })
    }
  }
}, 100)

/**
 * Check if user should be redirected away from billing
 * Redirects if: no billing info OR not admin/support role
 */
const shouldRedirect: ComputedRef<boolean> = computed(() => {
  if (isDataLoading.value) return false

  return hasNoBillingInfo.value || !hasAdminOrSupport.value
})

/**
 * Watch for route changes and permission changes
 * Auto-redirect if user shouldn't access billing
 */
watch(
  [() => router.currentRoute.value.path, shouldRedirect],
  ([path, redirect]: [string, boolean]) => {
    if (redirect) {
      debouncedNavigate('/')
      return
    }

    // Default redirect to premium-products if accessing /billing directly
    if (!isDataLoading.value && path === '/billing' && hasAdminOrSupport.value) {
      router.push('/billing/premium-products')
    }
  },
  { immediate: true }
)
</script>
